import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { getPublishedContent } from '../../services/contentService';
import { motion } from "framer-motion";
import { Sparkles, Star, ChevronRight } from "lucide-react";
import Navbar from '../../components/navbar/Navbar';
import HeroSearchBar from '../../components/HeroSearchBar';

// ------------------ PRODUCT IMAGE COMPONENT ------------------
const ProductImage = ({ src, alt, priority = false }) => {
    const [imageError, setImageError] = useState(false);
    
    if (imageError || !src) {
        return (
            <div className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 lg:w-48 lg:h-48 bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 border border-gray-100 flex items-center justify-center">
                <svg 
                    width="32" 
                    height="32" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    className="text-gray-400 sm:w-10 sm:h-10 md:w-12 md:h-12"
                >
                    <path 
                        d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                    />
                </svg>
            </div>
        );
    }
    
    return (
        <div className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 lg:w-48 lg:h-48 bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 border border-gray-100 flex items-center justify-center">
            <img 
                src={src}
                alt={alt}
                className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-44 lg:h-44 object-contain"
                onError={() => setImageError(true)}
                loading={priority ? "eager" : "lazy"}
            />
        </div>
    );
};

// ------------------ PRODUCT TILE COMPONENT ------------------
const ProductTile = ({ 
    badge, 
    imageSrc, 
    headline, 
    subline, 
    rating, 
    reviewCount, 
    updated = "Aug '25", 
    insight, 
    ctaLink, 
    guideLink,
    priority = false 
}) => {
    return (
        <div className="group hover:scale-[1.02] transition-all duration-300 h-full">
            <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-300 p-4 sm:p-6 shadow-md hover:shadow-lg transition-all duration-300 h-full flex flex-col">
                {/* Badge */}
                <div className="mb-3 sm:mb-4">
                    <span className="inline-block bg-custom-100 text-white text-xs font-semibold px-2 py-1 sm:px-3 rounded-full">
                        {badge}
                    </span>
                </div>
                
                {/* Product Image */}
                <div className="mb-3 sm:mb-4 flex justify-center">
                    <ProductImage 
                        src={imageSrc}
                        alt={`${headline}: ${subline}`}
                        priority={priority}
                    />
                </div>
                
                {/* Headline */}
                <h3 className="text-lg sm:text-xl font-bold text-black mb-2 leading-tight" style={{fontFamily: 'system-ui, sans-serif'}}>
                    {headline}
                </h3>
                
                {/* Subline */}
                <p className="text-black/70 font-medium mb-2 sm:mb-3 text-sm sm:text-base">
                    {subline}
                </p>
                
                {/* Trust Strip - Review count temporarily hidden */}
                <div className="flex items-center gap-1 sm:gap-2 mb-3 sm:mb-4 text-xs sm:text-sm text-black/55">
                    <span className="text-[#ab3d22]">★</span>
                    <span>{rating}</span>
                    <span>•</span>
                    <span>Updated {updated}</span>
                </div>
                
                {/* Insight - grows to fill space */}
                <p className="text-gray-700 mb-4 sm:mb-6 text-xs sm:text-sm leading-relaxed flex-grow">
                    {insight}
                </p>
                
                {/* CTA Section - pushed to bottom */}
                <div className="mt-auto">
                    {/* CTA Button */}
                    <Link 
                        to={ctaLink}
                        className="group/btn inline-flex items-center justify-center w-full bg-[#ab3d22] text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-full text-xs sm:text-sm font-semibold hover:bg-[#8a2f1a] transition-all duration-200 mb-2 sm:mb-3"
                    >
                        Shop Now
                        <span className="ml-1 sm:ml-2 group-hover/btn:translate-x-0.5 transition-transform duration-200">→</span>
                    </Link>
                    
                    {/* Secondary Link */}
                    <div className="text-center">
                        <Link to={guideLink} className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 underline transition-colors">
                            See full guide
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ------------------ UI PRIMITIVES ------------------
const Container = ({ children, className = "" }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionTitle = ({ eyebrow, title, subtitle, className = "" }) => (
  <div className={`mb-8 text-center ${className}`}>
    {eyebrow && <div className="mb-3 text-sm tracking-widest text-gray-500 uppercase">{eyebrow}</div>}
    <h2 className="text-3xl md:text-4xl tracking-tight text-gray-900" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>{title}</h2>
    {subtitle && <p className="mt-3 text-gray-600 max-w-2xl mx-auto">{subtitle}</p>}
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-[#f8f8f8] px-3 py-1 text-xs font-medium text-gray-700 shadow-sm">{children}</span>
);

const Rating = ({ value }) => (
  <div className="flex items-center gap-1" aria-label={`Rating ${value} out of 5`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < Math.round(value) ? "fill-[#ab3d22] stroke-[#ab3d22]" : "stroke-gray-300"}`} />
    ))}
    <span className="ml-2 text-sm text-gray-700">{value.toFixed(1)}</span>
  </div>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-gray-300 bg-white p-5 shadow-md hover:shadow-lg transition-shadow ${className}`}>{children}</div>
);

const CTAButton = ({ label, href = "#", icon, variant = "primary", onClick, ...props }) => {
  const Component = href !== "#" ? Link : "button";
  const linkProps = href !== "#" ? { to: href } : { onClick };
  
  return (
    <Component
      {...linkProps}
      {...props}
      className={
        variant === "primary"
          ? "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white bg-[#ab3d22] hover:bg-[#8a2f1a] focus:outline-none focus:ring-2 focus:ring-[#ab3d22]/20"
          : "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100"
      }
    >
      {icon}
      {label}
    </Component>
  );
};

// ------------------ OFFER CARD COMPONENT ------------------
const OfferCard = ({
    statusBadge,
    headline,
    subline,
    editorNote,
    lastChecked,
    imageSrc,
    imageAlt,
    merchantLogoSrc,
    primaryHref,
    secondaryHref,
    ctaLabel = "Claim Offer →",
    priority = false
}) => {
    const [imageError, setImageError] = useState(false);
    const [logoError, setLogoError] = useState(false);

    return (
        <div className="group relative bg-[#f8f8f8] border-l-4 border-[#ab3d22] rounded-r-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
            {/* Header strip with badge and logo */}
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-[#ab3d22] uppercase tracking-wide">
                        {statusBadge}
                    </span>
                    {merchantLogoSrc && !logoError && (
                        <div className="h-5 w-5 opacity-60">
                            <img
                                src={merchantLogoSrc}
                                alt="Merchant logo"
                                className="h-full w-full object-contain"
                                onError={() => setLogoError(true)}
                                loading="lazy"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Main content */}
            <div className="p-6">
                {/* Small product image - rectangular, not square */}
                <div className="float-right ml-4 mb-3 w-16 h-12 bg-white rounded border border-gray-200 flex items-center justify-center flex-shrink-0">
                    {imageError || !imageSrc ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-gray-300">
                            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                            <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                            <polyline points="21,15 16,10 5,21" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                    ) : (
                        <img
                            src={imageSrc}
                            alt={imageAlt}
                            className="w-12 h-8 object-contain"
                            onError={() => setImageError(true)}
                            loading={priority ? "eager" : "lazy"}
                            sizes="64px"
                        />
                    )}
                </div>

                {/* Headline - newspaper style */}
                <h3 className="text-lg font-bold text-black leading-tight mb-2" style={{fontFamily: 'system-ui, sans-serif'}}>
                    {headline}
                </h3>
                
                {/* Subline */}
                <p className="text-sm text-black/70 mb-3">{subline}</p>
                
                {/* Editor note */}
                <p className="text-sm text-black/85 leading-relaxed mb-4">{editorNote}</p>
                
                {/* Bottom row: trust strip + CTA */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="text-xs text-black/50">
                        Last checked: {lastChecked}
                    </div>
                    <div className="flex items-center gap-3">
                        {secondaryHref && (
                            <Link 
                                to={secondaryHref} 
                                className="text-xs text-black/60 hover:text-black underline transition-colors"
                            >
                                Details
                            </Link>
                        )}
                        <Link
                            to={primaryHref}
                            className="inline-flex items-center px-3 py-1.5 bg-[#ab3d22] text-white text-xs font-medium rounded hover:bg-[#8a2f1a] transition-colors"
                            data-origin="home-deals"
                            data-offer={headline.toLowerCase().replace(/\s+/g, '-')}
                        >
                            {ctaLabel}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};


// ------------------ BEAUTY INSIGHTS DATA ------------------
const beautyInsights = [
    {
        statusBadge: "New Guide",
        headline: "The Complete Retinol Guide: From Beginner to Pro",
        subline: "Skincare Fundamentals • 8 min read • 12/15/24",
        editorNote: "Everything you need to know about retinol—from choosing your first product to advanced layering techniques.",
        lastChecked: "Dec '24",
        imageSrc: "/images/guides/retinol-guide.webp",
        imageAlt: "Complete retinol skincare guide",
        merchantLogoSrc: "/images/logos/radiance-badge.svg",
        primaryHref: "/guides/retinol-complete-guide",
        secondaryHref: "/category/skincare/anti-aging",
        ctaLabel: "Read Guide →"
    },
    {
        statusBadge: "Trending",
        headline: "Korean vs Western Skincare: Which Routine Wins?",
        subline: "Skincare Fundamentals • 7 min read • 12/10/24",
        editorNote: "We tested both approaches for 30 days—here's what actually works and what's just hype.",
        lastChecked: "Dec '24",
        imageSrc: "/images/guides/korean-vs-western.webp",
        imageAlt: "Korean vs Western skincare comparison",
        merchantLogoSrc: "/images/logos/radiance-badge.svg",
        primaryHref: "/guides/korean-vs-western-skincare",
        secondaryHref: "/guides/routine-builder",
        ctaLabel: "See Results →"
    },
    {
        statusBadge: "Expert Pick",
        headline: "Drugstore Dupes That Actually Work",
        subline: "Budget Beauty • 6 min read • 12/5/24",
        editorNote: "High-end results without the price tag—our lab tested these drugstore alternatives.",
        lastChecked: "Dec '24",
        imageSrc: "/images/guides/drugstore-dupes.webp",
        imageAlt: "Best drugstore beauty dupes",
        merchantLogoSrc: "/images/logos/expert-badge.svg",
        primaryHref: "/guides/drugstore-dupes-that-work",
        secondaryHref: "/guides/budget-beauty",
        ctaLabel: "Save Money →"
    },
    {
        statusBadge: "Seasonal",
        headline: "Winter Skin SOS: Fix Dryness in 7 Days",
        subline: "Problem-Solving • 5 min read • 12/1/24",
        editorNote: "Combat winter dryness with this proven 7-day protocol—see visible improvement fast.",
        lastChecked: "Dec '24",
        imageSrc: "/images/guides/winter-skin-sos.webp",
        imageAlt: "Winter skincare routine guide",
        merchantLogoSrc: "/images/logos/derm-badge.svg",
        primaryHref: "/guides/winter-skin-sos",
        secondaryHref: "/category/skincare/dry-skin",
        ctaLabel: "Fix Skin →"
    },
    {
        statusBadge: "Popular",
        headline: "Ingredient Spotlight: Vitamin C vs Niacinamide",
        subline: "Ingredients • 9 min read • 11/28/24",
        editorNote: "The ultimate guide to these powerhouse ingredients—including how to layer them safely.",
        lastChecked: "Dec '24",
        imageSrc: "/images/guides/vitamin-c-niacinamide.webp",
        imageAlt: "Vitamin C and Niacinamide guide",
        merchantLogoSrc: "/images/logos/science-badge.svg",
        primaryHref: "/guides/vitamin-c-vs-niacinamide",
        secondaryHref: "/guides/ingredient-layering",
        ctaLabel: "Learn More →"
    },
    {
        statusBadge: "Must-Read",
        headline: "TikTok Beauty Trends: What Actually Works?",
        subline: "Viral Trends • 6 min read • 11/25/24",
        editorNote: "We tested the biggest TikTok beauty trends so you don't have to—here's what's worth trying.",
        lastChecked: "Dec '24",
        imageSrc: "/images/guides/tiktok-trends.webp",
        imageAlt: "TikTok beauty trends analysis",
        merchantLogoSrc: "/images/logos/trend-badge.svg",
        primaryHref: "/guides/tiktok-beauty-trends-tested",
        secondaryHref: "/trending/viral-trends",
        ctaLabel: "Get Truth →"
    }
];

// ------------------ PRODUCT DATA ------------------
// Static fallback data (used as backup if no featured products)
// Only using admin-approved badges: editors_choice, derm_approved, best_seller, trending
const fallbackEditorChoiceProducts = [
    {
        badge: "Editor's Choice",
        imageSrc: "/images/products/packshots/cerave-moisturizing-cream-front.webp",
        headline: "Best Moisturizer for Dry Skin",
        subline: "CeraVe Moisturizing Cream",
        rating: "4.8",
        reviewCount: "32k+ reviews",
        insight: "Packed with ceramides and hyaluronic acid to restore your skin barrier—without feeling greasy.",
        ctaLink: "/category/skincare",
        guideLink: "/category/skincare"
    },
    {
        badge: "Derm Approved",
        imageSrc: "/images/products/packshots/la-roche-posay-vitamin-c10-front.webp",
        headline: "Best Vitamin C Serum of 2025",
        subline: "La Roche-Posay Pure Vitamin C10 Serum",
        rating: "4.6",
        reviewCount: "9.2k+ reviews",
        insight: "Gentle, fragrance-free, and proven to reduce dullness and dark spots—even on sensitive skin.",
        ctaLink: "/category/skincare",
        guideLink: "/category/skincare"
    },
    {
        badge: "Best Seller",
        imageSrc: "/images/products/packshots/eltamd-uv-clear-spf46-front.webp",
        headline: "Best SPF for Oily Skin",
        subline: "EltaMD UV Clear Broad-Spectrum SPF 46",
        rating: "4.7",
        reviewCount: "15k+ reviews",
        insight: "Lightweight and calming with niacinamide—perfect under makeup and for acne-prone skin.",
        ctaLink: "/category/skincare",
        guideLink: "/category/skincare"
    },
    {
        badge: "Trending",
        imageSrc: "/images/products/packshots/kerastase-genesis-bain-front.webp",
        headline: "Best Shampoo for Hair Fall",
        subline: "Kérastase Genesis Bain Nutri-Fortifiant",
        rating: "4.5",
        reviewCount: "3.5k+ reviews",
        insight: "Strengthens weak hair at the root, reduces breakage, and smells like a luxury salon visit.",
        ctaLink: "/category/hair",
        guideLink: "/category/hair"
    },
    {
        badge: "Editor's Choice",
        imageSrc: "/images/products/packshots/youth-to-people-superfood-cleanser-front.webp",
        headline: "Best Cleanser for Acne-Prone Skin",
        subline: "Youth to the People Superfood Cleanser",
        rating: "4.6",
        reviewCount: "7.8k+ reviews",
        insight: "Gently removes dirt and oil with kale + green tea, without drying or stripping.",
        ctaLink: "/category/skincare",
        guideLink: "/category/skincare"
    },
    {
        badge: "Best Seller",
        imageSrc: "/images/products/packshots/the-ordinary-hyaluronic-acid-front.webp",
        headline: "Best Budget Hydrator Under $20",
        subline: "The Ordinary Hyaluronic Acid 2% + B5",
        rating: "4.7",
        reviewCount: "45k+ reviews",
        insight: "Layers under any routine, instantly plumps, and costs less than lunch.",
        ctaLink: "/category/skincare",
        guideLink: "/category/skincare"
    }
];


// Blog cards data
const blogCards = [
    {
        id: 1,
        badge: "NEW & TRENDING",
        title: "latest beauty trends",
        description: "Discover what's hot in skincare, makeup, and wellness. Get insider tips before they go viral.",
        bgColor: "bg-gray-800",
        textColor: "text-white",
        badgeColor: "bg-black",
        image: "/images/woman-applying-facemask.png",
        ctaText: "READ MORE",
        hoverColor: "hover:text-gray-300"
    },
    {
        id: 2,
        badge: "SPOTLIGHT",
        title: "Ingredient Deep Dives",
        description: "Learn the science behind powerhouse ingredients and how to use them effectively in your routine.",
        bgColor: "bg-gradient-to-br from-orange-400 to-orange-500",
        textColor: "text-white",
        badgeColor: "bg-black",
        ctaText: "EXPLORE NOW",
        hoverColor: "hover:text-orange-200",
        decorativeElements: (
            <div className="mb-6 flex gap-3">
                <div className="w-12 h-16 bg-[#f8f8f8] rounded-lg opacity-90"></div>
                <div className="w-12 h-16 bg-[#f8f8f8] rounded-lg opacity-80"></div>
                <div className="w-12 h-16 bg-[#f8f8f8] rounded-lg opacity-70"></div>
            </div>
        )
    },
    {
        id: 3,
        badge: "VIRAL",
        title: "Worth the Hype",
        description: "We test viral beauty products so you don't have to. Real reviews, honest results.",
        bgColor: "bg-gradient-to-br from-red-500 to-red-600",
        textColor: "text-white",
        badgeColor: "bg-black",
        ctaText: "SHOP NOW",
        hoverColor: "hover:text-red-200",
        decorativeElements: (
            <div className="mb-6 grid grid-cols-3 gap-2">
                <div className="w-full h-12 bg-[#f8f8f8] rounded opacity-90"></div>
                <div className="w-full h-12 bg-[#f8f8f8] rounded opacity-80"></div>
                <div className="w-full h-12 bg-[#f8f8f8] rounded opacity-85"></div>
                <div className="w-full h-12 bg-[#f8f8f8] rounded opacity-75"></div>
                <div className="w-full h-12 bg-[#f8f8f8] rounded opacity-90"></div>
                <div className="w-full h-12 bg-[#f8f8f8] rounded opacity-70"></div>
            </div>
        )
    },
    {
        id: 4,
        badge: "EXPERT TIPS",
        title: "skincare routines",
        description: "Step-by-step guides to building the perfect skincare routine for every skin type and concern.",
        bgColor: "bg-gradient-to-br from-blue-500 to-blue-600",
        textColor: "text-white",
        badgeColor: "bg-black",
        ctaText: "LEARN MORE",
        hoverColor: "hover:text-blue-200",
        decorativeElements: (
            <div className="mb-6">
                <div className="w-20 h-20 bg-[#f8f8f8] rounded-full opacity-80"></div>
            </div>
        )
    },
    {
        id: 5,
        badge: "SEASONAL",
        title: "summer beauty must-haves",
        description: "Get ready for the season with our curated selection of summer-perfect beauty essentials.",
        bgColor: "bg-gradient-to-br from-green-500 to-green-600",
        textColor: "text-white",
        badgeColor: "bg-black",
        ctaText: "DISCOVER",
        hoverColor: "hover:text-green-200",
        decorativeElements: (
            <div className="mb-6 flex gap-2">
                <div className="w-8 h-16 bg-[#f8f8f8] rounded-full opacity-90"></div>
                <div className="w-8 h-16 bg-[#f8f8f8] rounded-full opacity-70"></div>
                <div className="w-8 h-16 bg-[#f8f8f8] rounded-full opacity-85"></div>
            </div>
        )
    },
    {
        id: 6,
        badge: "REVIEWS",
        title: "honest product reviews",
        description: "Unbiased reviews from real users. Find out what actually works before you spend your money.",
        bgColor: "bg-gradient-to-br from-purple-500 to-purple-600",
        textColor: "text-white",
        badgeColor: "bg-black",
        ctaText: "READ REVIEWS",
        hoverColor: "hover:text-purple-200",
        decorativeElements: (
            <div className="mb-6 flex gap-1">
                <div className="w-4 h-4 bg-[#f8f8f8] rounded-full opacity-90"></div>
                <div className="w-4 h-4 bg-[#f8f8f8] rounded-full opacity-80"></div>
                <div className="w-4 h-4 bg-[#f8f8f8] rounded-full opacity-70"></div>
                <div className="w-4 h-4 bg-[#f8f8f8] rounded-full opacity-85"></div>
                <div className="w-4 h-4 bg-[#f8f8f8] rounded-full opacity-95"></div>
            </div>
        )
    },
    {
        id: 7,
        badge: "ROUTINE GUIDE",
        title: "build your perfect routine",
        description: "Step-by-step guides for every skin type. Create a routine that actually works for your lifestyle.",
        bgColor: "bg-gradient-to-br from-blue-500 to-blue-600",
        textColor: "text-white",
        badgeColor: "bg-black",
        ctaText: "BUILD ROUTINE",
        hoverColor: "hover:text-blue-200",
        decorativeElements: (
            <div className="mb-6 flex gap-2">
                <div className="w-6 h-12 bg-[#f8f8f8] rounded-lg opacity-90"></div>
                <div className="w-6 h-12 bg-[#f8f8f8] rounded-lg opacity-75"></div>
                <div className="w-6 h-12 bg-[#f8f8f8] rounded-lg opacity-85"></div>
            </div>
        )
    },
    {
        id: 8,
        badge: "BUDGET BEAUTY",
        title: "drugstore vs high-end",
        description: "Get luxury results without the price tag. Our tested dupes and budget alternatives that actually work.",
        bgColor: "bg-gradient-to-br from-custom-100 to-custom-200",
        textColor: "text-white",
        badgeColor: "bg-black",
        ctaText: "SAVE MONEY",
        hoverColor: "hover:text-custom-100/80",
        decorativeElements: (
            <div className="mb-6 flex gap-3">
                <div className="w-8 h-8 bg-[#f8f8f8] rounded-full opacity-90 flex items-center justify-center">
                    <div className="w-4 h-4 bg-[#ab3d22] rounded-full"></div>
                </div>
                <div className="w-8 h-8 bg-[#f8f8f8] rounded-full opacity-75 flex items-center justify-center">
                    <div className="w-4 h-4 bg-[#ab3d22] rounded-full"></div>
                </div>
            </div>
        )
    },
    {
        id: 9,
        badge: "PROBLEM SOLVING",
        title: "skin concerns solved",
        description: "Target specific issues with expert-backed solutions. From acne to aging, we've got you covered.",
        bgColor: "bg-gradient-to-br from-red-500 to-red-600",
        textColor: "text-white",
        badgeColor: "bg-black",
        ctaText: "FIX ISSUES",
        hoverColor: "hover:text-red-200",
        decorativeElements: (
            <div className="mb-6 grid grid-cols-2 gap-2 w-fit">
                <div className="w-6 h-6 bg-[#f8f8f8] rounded opacity-90"></div>
                <div className="w-6 h-6 bg-[#f8f8f8] rounded opacity-80"></div>
                <div className="w-6 h-6 bg-[#f8f8f8] rounded opacity-75"></div>
                <div className="w-6 h-6 bg-[#f8f8f8] rounded opacity-85"></div>
            </div>
        )
    }
];

const HomePage = () => {
    const { categories, products, loading } = useData();
    const [email, setEmail] = useState('');
    const [activeTab, setActiveTab] = useState('');
    const [currentSlide, setCurrentSlide] = useState(0);
    const [blogPosts, setBlogPosts] = useState([]);
    const [blogLoading, setBlogLoading] = useState(true);
    
    // Set initial active tab when categories are loaded
    React.useEffect(() => {
        if (categories.length > 0 && !activeTab) {
            setActiveTab(categories[0].slug);
        }
    }, [categories, activeTab]);

    // Fetch blog posts for "Stay in the Know" section
    useEffect(() => {
        const fetchBlogPosts = async () => {
            try {
                setBlogLoading(true);
                const response = await getPublishedContent('blog');
                if (response && response.data) {
                    // Sort by most recent first and take only 5 posts
                    const sortedPosts = response.data
                        .sort((a, b) => new Date(b.publishedAt || b.createdAt) - new Date(a.publishedAt || a.createdAt))
                        .slice(0, 5);
                    setBlogPosts(sortedPosts);
                } else {
                    setBlogPosts([]);
                }
            } catch (error) {
                console.error('Error fetching blog posts:', error);
                setBlogPosts([]);
            } finally {
                setBlogLoading(false);
            }
        };

        fetchBlogPosts();
    }, []);


    // Transform blog posts into the format expected by the insights section
    const transformBlogPostsToInsights = (posts) => {
        return posts.slice(0, 4).map((post, index) => {
            console.log(`Home page - Processing post ${index}:`, post.title, 'Image:', post.image);
            
            // Format read time - ensure it always has "min read"
            let readTime = post.readTime || '5 min read';
            if (readTime && !readTime.includes('read')) {
                readTime = `${readTime} read`;
            }
            
            // Format date as M/D/YY (e.g., "9/2/24")
            const dateObj = new Date(post.publishedAt || post.createdAt);
            const formattedDate = `${dateObj.getMonth() + 1}/${dateObj.getDate()}/${dateObj.getFullYear().toString().slice(-2)}`;
            
            // Handle image path - support both relative and absolute paths
            let imagePath = post.image || `/images/hero-face.png`;
            // If image path doesn't start with http or /, assume it needs a prefix
            if (imagePath && !imagePath.startsWith('http') && !imagePath.startsWith('/')) {
                imagePath = `/${imagePath}`;
            }
            console.log(`Final image path for "${post.title}":`, imagePath);
            
            return {
                statusBadge: post.tags && post.tags.length > 0 ? post.tags[0] : "New Post",
                headline: post.title,
                subline: `${post.eyebrow || 'Blog'} • ${readTime} • ${formattedDate}`,
                editorNote: post.description || "Expert insights and tips from the Radiance team.",
                lastChecked: new Date(post.updatedAt || post.createdAt).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                imageSrc: imagePath,
                imageAlt: post.title,
                merchantLogoSrc: "/images/logos/radiance-badge.svg",
                primaryHref: `/blog/${post.slug}`,
                secondaryHref: "/blog",
                ctaLabel: "Read Article →"
            };
        });
    };

    // Transform blog posts for the bottom row cards
    const transformBlogPostsToCards = (posts) => {
        const colors = [
            { bgColor: "bg-gray-800", textColor: "text-white", badgeColor: "bg-black" },
            { bgColor: "bg-[#ab3d22]", textColor: "text-white", badgeColor: "bg-black" },
            { bgColor: "bg-[#f39c12]", textColor: "text-white", badgeColor: "bg-black" }
        ];

        // Use the 5th post if available, otherwise return empty array
        const fifthPost = posts[4];
        if (!fifthPost) return [];

        return [{
            id: fifthPost._id,
            badge: fifthPost.tags && fifthPost.tags.length > 0 ? fifthPost.tags[0].toUpperCase() : "LATEST",
            title: fifthPost.title.toLowerCase(),
            description: fifthPost.description || "Expert insights and tips from the Radiance team.",
            ...colors[0], // Use first color scheme
            image: fifthPost.image || "/images/hero-face.png", // Use actual blog image or fallback
            ctaText: "READ MORE",
            href: `/blog/${fifthPost.slug}`
        }];
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Redirect to Mailchimp subscription form
        window.open('http://eepurl.com/jpTPXQ', '_blank', 'noopener,noreferrer');
        setEmail('');
    };

    // Carousel navigation functions
    const maxSlides = blogCards.length - 3; // Maximum number of slides (9 cards - 3 visible = 6 positions: 0, 1, 2, 3, 4, 5, 6)
    
    const nextSlide = () => {
        if (currentSlide < maxSlides) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    // Helper function to convert backend badge codes to display labels
    const badgeLabel = (b) => {
        switch (b) {
            case 'editors_choice':
                return "Editor's Choice";
            case 'derm_approved':
                return 'Derm Approved';
            case 'best_seller':
                return 'Best Seller';
            case 'trending':
                return 'Trending';
            default:
                return b;
        }
    };

    // Helper function to transform backend products to frontend format
    const transformProductForDisplay = (product, index) => {
        // Use actual badge from admin (take the first one if multiple)
        const badge = product.badges && product.badges.length > 0 
            ? badgeLabel(product.badges[0]) 
            : "Editor's Choice"; // Fallback to admin-approved badge
        
        // Create navigation link - use subcategory if available, otherwise category
        let ctaLink;
        if (product.subcategory?.slug && product.category?.slug) {
            // Go to specific subcategory page
            ctaLink = `/category/${product.category.slug}/${product.subcategory.slug}#product-${product._id}`;
        } else if (product.category?.slug) {
            // Go to category page
            ctaLink = `/category/${product.category.slug}#product-${product._id}`;
        } else {
            // Fallback to skincare category
            ctaLink = `/category/skincare#product-${product._id}`;
        }
        
        // Debug: Log the navigation data
        console.log(`🔍 PRODUCT NAVIGATION DEBUG - ${product.name}:`, {
            productName: product.name,
            category: product.category,
            subcategory: product.subcategory,
            categorySlug: product.category?.slug,
            subcategorySlug: product.subcategory?.slug,
            finalLink: ctaLink,
            productId: product._id
        });
        
        return {
            badge,
            imageSrc: product.imageUrl || "/images/products/packshots/placeholder.webp",
            headline: product.name.startsWith(product.brand) 
                ? product.name 
                : `${product.brand} ${product.name}`,
            subline: product.name,
            rating: product.rating ? Number(product.rating).toFixed(1) : "4.0",
            reviewCount: `${Math.floor(Math.random() * 50 + 10)}k+ reviews`,
            insight: product.description?.length > 100 
                ? product.description.substring(0, 100) + "..." 
                : product.description || "Premium quality product recommended by our experts.",
            ctaLink,
            guideLink: ctaLink
        };
    };

    // ============ HOMEPAGE "TRIED, TESTED, TRULY LOVED" SECTION ============
    // This section shows products based on BADGES (derm_approved, trending, editors_choice, best_seller)
    // NOT based on isFeatured (which is used for category/subcategory Featured Picks)
    
    console.log('🔍 HOME PAGE - "Tried, Tested, Truly Loved" Section Debug:', {
        totalProducts: products.length,
        productsWithBadges: products.filter(p => p.badges && p.badges.length > 0).length,
        dermApprovedCount: products.filter(p => p.badges?.includes('derm_approved')).length,
        trendingCount: products.filter(p => p.badges?.includes('trending')).length,
        editorsChoiceCount: products.filter(p => p.badges?.includes('editors_choice')).length,
        bestSellerCount: products.filter(p => p.badges?.includes('best_seller')).length
    });

    // Log a sample of products to see their structure
    if (products.length > 0 && !loading) {
        console.log('📦 Sample product structure:', {
            firstProduct: {
                name: products[0].name,
                badges: products[0].badges,
                category: products[0].category,
                subcategory: products[0].subcategory
            }
        });
        
        // Log all products with badges
        const allBadgedProducts = products.filter(p => p.badges && p.badges.length > 0);
        if (allBadgedProducts.length > 0) {
            console.log('🏷️ Products with badges:', allBadgedProducts.map(p => ({
                name: p.name,
                badges: p.badges
            })));
        } else {
            console.warn('❌ NO PRODUCTS WITH BADGES FOUND! To fix: Go to Admin > Products > Edit a product > Select badges (Derm Approved, Trending, Editor\'s Choice, Best Seller)');
        }
    }

    // Get products with badges - these will appear in the "Tried, Tested, Truly Loved" section
    // Priority order: derm_approved, trending, editors_choice, best_seller
    let badgedProductsForDisplay = [];
    
    // Get products for each badge type
    const dermApproved = products.filter(p => p.badges?.includes('derm_approved'));
    const trending = products.filter(p => p.badges?.includes('trending'));
    const editorsChoice = products.filter(p => p.badges?.includes('editors_choice'));
    const bestSeller = products.filter(p => p.badges?.includes('best_seller'));
    
    // Mix them up for variety - take 2 from each category if available
    const productsByBadge = [
        ...dermApproved.slice(0, 2),
        ...trending.slice(0, 2),
        ...editorsChoice.slice(0, 2),
        ...bestSeller.slice(0, 2)
    ];
    
    // Remove duplicates (in case a product has multiple badges)
    const uniqueProducts = Array.from(new Map(productsByBadge.map(p => [p._id, p])).values());
    
    // If we still don't have 6, add any remaining badged products
    if (uniqueProducts.length < 6) {
        const allBadged = products.filter(p => p.badges && p.badges.length > 0);
        const remaining = allBadged.filter(p => !uniqueProducts.find(up => up._id === p._id));
        badgedProductsForDisplay = [...uniqueProducts, ...remaining].slice(0, 6);
    } else {
        badgedProductsForDisplay = uniqueProducts.slice(0, 6);
    }
    
    console.log(`✅ Found ${badgedProductsForDisplay.length} products with badges for homepage`);
    console.log('🎯 Badge distribution:', {
        derm_approved: badgedProductsForDisplay.filter(p => p.badges?.includes('derm_approved')).length,
        trending: badgedProductsForDisplay.filter(p => p.badges?.includes('trending')).length,
        editors_choice: badgedProductsForDisplay.filter(p => p.badges?.includes('editors_choice')).length,
        best_seller: badgedProductsForDisplay.filter(p => p.badges?.includes('best_seller')).length
    });
    
    // Only use fallback if NO badged products exist at all
    const editorChoiceProducts = badgedProductsForDisplay.length > 0 
        ? badgedProductsForDisplay.map(transformProductForDisplay)
        : fallbackEditorChoiceProducts;
    
    // Log warning if using fallback data
    if (badgedProductsForDisplay.length === 0 && !loading) {
        console.warn('⚠️ HOME PAGE: No products with badges found in database. Using fallback data.');
        console.warn('📝 TO FIX: Go to Admin Panel > Products > Click a product > Select badges:');
        console.warn('   • Derm Approved - for dermatologist-recommended products');
        console.warn('   • Trending - for trending items');
        console.warn('   • Editor\'s Choice - for editorial picks');
        console.warn('   • Best Seller - for popular products');
        console.warn('   Note: The "Featured Product" checkbox is for category/subcategory pages, NOT the homepage!');
    } else if (badgedProductsForDisplay.length > 0) {
        console.log('✨ Using REAL badged products from database (not fallback)');
    }
    // ============ END HOMEPAGE SECTION ============

    // Get trending products (non-featured, recent products)
    const trendingProducts = products.filter(product => !product.isFeatured).slice(0, 6);



    // REMOVED: Loading spinner - page now renders instantly with fallback data
    // Real data hydrates seamlessly in the background

    return (
        <div className="bg-[#f8f8f8] text-gray-900">
            {/* Use shared Navbar component with dropdown functionality */}
            <Navbar />

            {/* ============================================ */}
            {/* HERO SECTION - Premium, Editorial Layout */}
            {/* ============================================ */}
            <section className="relative bg-[#f8f8f8] pt-8 sm:pt-10 lg:pt-12 pb-10 sm:pb-12 lg:pb-14">
                <Container>
                    <div className="grid lg:grid-cols-12 items-center gap-12 lg:gap-16 xl:gap-24">
                        {/* Left Column - Text + Search */}
                        <div className="lg:col-span-7 text-center lg:text-left">
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                                <h1 
                                    className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-black leading-[1.1]" 
                                    style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}
                                >
                                    discover<br />
                                    your <span className="text-[#ab3d22]">radiance.</span>
                                </h1>
                                <p className="mt-6 sm:mt-8 text-lg sm:text-xl text-gray-600 max-w-xl leading-relaxed">
                                    We cut through the noise and simplify your search for the best products — so you always know what's worth buying.
                                </p>
                                {/* Search Bar */}
                                <div className="mt-6 sm:mt-8">
                                    <HeroSearchBar />
                                    <p className="mt-3 text-sm text-gray-500">
                                        Not sure where to start? <a href="#categories" className="text-[#ab3d22] font-medium hover:underline">Browse categories below.</a>
                                    </p>
                                </div>
                            </motion.div>
                        </div>

                        {/* Right Column - Hero Image */}
                        <div className="lg:col-span-5 flex justify-center lg:justify-end">
                            <div className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl">
                                <img 
                                    className="w-full h-80 sm:h-96 lg:h-[450px] xl:h-[500px] object-cover rounded-3xl shadow-2xl" 
                                    src="/images/glowing-face.png" 
                                    alt="Radiance hero preview" 
                                />
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Credibility Block */}
            <section className="py-8 sm:py-10 lg:py-12 bg-[#f8f8f8]">
                <Container>
                    <div className="max-w-5xl mx-auto text-center">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                            Clarity for the way people really choose.
                        </h2>
                        <p className="text-base sm:text-lg text-gray-600 mb-10 sm:mb-12 leading-relaxed max-w-3xl mx-auto">
                            Radiance makes beauty and wellness easier to navigate by listening to real experiences, highlighting what consistently works, and creating space for people to learn from one another.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row sm:justify-center sm:divide-x sm:divide-gray-300 gap-8 sm:gap-0">
                            <div className="sm:px-10 lg:px-12 first:sm:pl-0 last:sm:pr-0">
                                <h3 className="text-base font-semibold text-gray-900 mb-1" style={{fontFamily: 'Poppins, sans-serif'}}>
                                    Radiance Score
                                </h3>
                                <p className="text-sm text-gray-600 leading-relaxed max-w-[220px] mx-auto">
                                    Ratings shaped by consistency, performance, and real routines.
                                </p>
                            </div>
                            
                            <div className="sm:px-10 lg:px-12">
                                <h3 className="text-base font-semibold text-gray-900 mb-1" style={{fontFamily: 'Poppins, sans-serif'}}>
                                    Thoughtfully Reviewed
                                </h3>
                                <p className="text-sm text-gray-600 leading-relaxed max-w-[280px] mx-auto">
                                    Reviewed with care — focusing on feel, fit, and what actually matters.
                                </p>
                            </div>
                            
                            <div className="sm:px-10 lg:px-12 first:sm:pl-0 last:sm:pr-0">
                                <h3 className="text-base font-semibold text-gray-900 mb-1" style={{fontFamily: 'Poppins, sans-serif'}}>
                                    Community-Led <span className="text-xs text-[#ab3d22] font-medium">(Coming Soon)</span>
                                </h3>
                                <p className="text-sm text-gray-600 leading-relaxed max-w-[220px] mx-auto">
                                    Built around shared routines and honest experience.
                                </p>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* ============================================ */}
            {/* CATEGORIES SECTION - Card Components */}
            {/* ============================================ */}
            <section id="categories" className="py-8 sm:py-10 lg:py-12 bg-[#f8f8f8]">
                <Container>
                    {/* Section Header */}
                    <div className="mb-8 sm:mb-10">
                        <div className="mb-3 text-xs sm:text-sm tracking-widest text-gray-500 uppercase">Explore</div>
                        <h2 
                            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3"
                            style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}
                        >
                            Browse by Category
                        </h2>
                        <p className="text-base sm:text-lg text-gray-600 max-w-2xl">
                            Not sure where to start? Browse trusted collections designed to help you choose confidently.
                        </p>
                    </div>
                    
                    {/* Category Cards Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
                        {categories.map(category => {
                            const categoryProducts = products.filter(product => 
                                product.category?._id === category._id || product.category === category._id
                            );
                            
                            // Category descriptions
                            const categoryDescriptions = {
                                skincare: "Cleansers, moisturizers, serums, and treatments for every skin type and concern.",
                                makeup: "Foundation, concealer, lipstick, and color cosmetics for every look.",
                                hair: "Shampoos, conditioners, styling products, and treatments for healthy hair.",
                                body: "Body lotions, scrubs, oils, and treatments for smooth, nourished skin.",
                                wellness: "Supplements, aromatherapy, and self-care products for holistic wellbeing.",
                                health: "Health essentials and supplements reviewed with science and safety in mind.",
                                fragrance: "Perfumes, colognes, and scented products for every occasion."
                            };
                            
                            const description = categoryDescriptions[category.slug] || `Discover our curated selection of ${category.name.toLowerCase()} products.`;
                            
                            return (
                                <Link 
                                    key={category._id} 
                                    to={`/category/${category.slug}`} 
                                    className="group"
                                >
                                    <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 shadow-sm hover:shadow-xl hover:border-[#ab3d22]/20 transition-all duration-300 h-full flex flex-col">
                                        {/* Header Row */}
                                        <div className="flex items-start justify-between mb-4">
                                            <h3 
                                                className="text-xl sm:text-2xl font-bold text-gray-900 group-hover:text-[#ab3d22] transition-colors"
                                                style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}
                                            >
                                                {category.name}
                                            </h3>
                                            <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-[#ab3d22]/10 flex items-center justify-center transition-colors flex-shrink-0 ml-4">
                                                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-[#ab3d22] group-hover:translate-x-0.5 transition-all" />
                                            </div>
                                        </div>
                                        
                                        {/* Description */}
                                        <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6 flex-grow">
                                            {description}
                                        </p>
                                        
                                        {/* Product Count */}
                                        <div className="pt-4 border-t border-gray-100">
                                            <span className="text-sm font-medium text-gray-500">
                                                {categoryProducts.length}+ products
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </Container>
            </section>


            {/* Our Top Picks */}
            <section id="top-picks" className="py-8 sm:py-10 lg:py-12 bg-[#f8f8f8]">
                <Container>
                    <div className="mb-8 sm:mb-10">
                        <div className="mb-3 text-xs sm:text-sm tracking-widest text-gray-500 uppercase">Our Top Picks</div>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                            Top Picks — Chosen for You
                        </h2>
                        <p className="text-base sm:text-lg text-gray-600 max-w-2xl">
                            These are the products that consistently rise to the top. No guesswork — just our best recommendations.
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mx-auto max-w-7xl auto-rows-fr">
                        {editorChoiceProducts.map((product, index) => (
                            <div key={product.headline} className="flex">
                                <ProductTile
                                    badge={product.badge}
                                    imageSrc={product.imageSrc}
                                    headline={product.headline}
                                    subline={product.subline}
                                    rating={product.rating}
                                    reviewCount={product.reviewCount}
                                    insight={product.insight}
                                    ctaLink={product.ctaLink}
                                    guideLink={product.guideLink}
                                    priority={index === 0}
                                />
                            </div>
                        ))}
                    </div>
                </Container>
            </section>

            {/* Beauty Insights & Guides */}
            <section className="py-8 sm:py-10 lg:py-12 bg-[#f8f8f8]">
                <Container>
                    <div className="mb-8 sm:mb-10">
                        <div className="mb-3 text-xs sm:text-sm tracking-widest text-gray-500 uppercase">Editorial</div>
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                            <div>
                                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                                    Learn Before You Buy
                                </h2>
                                <p className="text-base sm:text-lg text-gray-600 max-w-2xl">
                                    Quick reads to help you understand what works — and why.
                                </p>
                            </div>
                            <Link 
                                to="/blog" 
                                className="text-[#ab3d22] hover:text-[#8a2f1a] font-medium whitespace-nowrap"
                            >
                                View all →
                            </Link>
                        </div>
                    </div>
                    
                    {/* Unified Editorial Grid - 4 Featured Articles */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mx-auto max-w-7xl">
                        {blogLoading ? (
                            // Loading skeleton for articles
                            Array.from({ length: 4 }).map((_, index) => (
                                <div 
                                    key={index}
                                    className="animate-pulse bg-white rounded-xl sm:rounded-2xl border border-gray-300 p-4 sm:p-6 h-64 sm:h-80 shadow-md"
                                />
                            ))
                        ) : (
                            (blogPosts.length > 0 ? transformBlogPostsToInsights(blogPosts) : beautyInsights.slice(0, 4)).map((insight, index) => (
                            <Link 
                                key={insight.headline}
                                to={insight.primaryHref}
                                className="group bg-white rounded-xl sm:rounded-2xl border border-gray-300 overflow-hidden hover:shadow-lg hover:border-gray-400 transition-all duration-300 flex flex-col shadow-md h-full"
                            >
                                {/* Article Image */}
                                <div className="h-40 sm:h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                    {insight.imageSrc ? (
                                        <img 
                                            src={insight.imageSrc} 
                                            alt={insight.headline}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                    ) : (
                                        <div className="text-center p-4 sm:p-6">
                                            <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 ${
                                                index === 0 ? 'bg-[#ab3d22]' :
                                                index === 1 ? 'bg-gray-800' :
                                                index === 2 ? 'bg-[#f39c12]' :
                                                'bg-[#8e44ad]'
                                            }`}>
                                                <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Article Content */}
                                <div className="p-4 sm:p-6 flex flex-col">
                                    
                                    <h3 className="font-bold mb-2 sm:mb-3 leading-tight text-base sm:text-lg text-gray-900 group-hover:text-[#ab3d22] transition-colors line-clamp-2">
                                        {insight.headline}
                                    </h3>
                                    
                                    <p className="text-gray-600 text-xs sm:text-sm line-clamp-3 mb-4">
                                        {insight.editorNote}
                                    </p>

                                    {/* Metadata row with consistent formatting */}
                                    <div className="flex flex-col gap-2 text-xs text-gray-500 mt-auto pt-3 border-t border-gray-100">
                                        <span className="text-gray-600">{insight.subline}</span>
                                        <span className="text-[#ab3d22] font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 self-end">
                                            Read More <ChevronRight className="h-3 w-3" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))
                        )}
                    </div>
                </Container>
            </section>

            {/* Our Approach */}
            <section className="bg-[#f8f8f8]">
                <Container className="py-8 sm:py-10 lg:py-12">
                    <div className="flex flex-col lg:flex-row items-center justify-center max-w-6xl mx-auto gap-12 lg:gap-16">
                        <div className="flex justify-center lg:w-1/2">
                            <img
                                src="/images/u4279188898_A_close_up_of_a_women_of_smooth_skin_subtle_and_s_c9f02db3-ef4f-475c-8264-594d5b595999_0.png"
                                alt="Woman with radiant skin"
                                className="w-full max-w-[500px] h-auto rounded-3xl shadow-lg"
                            />
                        </div>
                        <div className="lg:w-1/2 text-center lg:text-left">
                            <h2 className="text-3xl md:text-4xl mb-6" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                                <span className="text-black">Our</span> <span className="text-[#ab3d22]">Approach</span>
                            </h2>
                            <div className="space-y-5 max-w-xl mx-auto lg:mx-0">
                                <p className="text-lg text-black leading-relaxed">
                                    Beauty is personal — and choosing products should feel supportive, not overwhelming.
                                </p>
                                <p className="text-lg text-black leading-relaxed">
                                    Radiance helps bring clarity by listening closely to real experiences and surfacing what actually works across different routines, skin types, and preferences.
                                </p>
                                <p className="text-lg text-black leading-relaxed">
                                    We're building toward a space shaped by community, where honest feedback and shared knowledge help everyone feel more confident in their choices.
                                </p>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Footer */}
            <footer className="bg-black">
                <Container className="py-12 sm:py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                        {/* LEFT HALF: Brand + Newsletter */}
                        <div className="lg:pr-8">
                            <Link 
                                to="/" 
                                className="text-2xl sm:text-3xl font-bold lowercase mb-6 inline-block hover:opacity-80 transition-opacity"
                                style={{ 
                                    fontFamily: 'Poppins, sans-serif', 
                                    fontWeight: 700,
                                    color: '#ffffff'
                                }}
                            >
                                <span style={{ color: '#ffffff' }}>radiance</span><span style={{ color: '#ab3d22' }}>.</span>
                            </Link>
                            
                            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                Your shortcut to smarter self-care.
                            </h3>
                            <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
                                Honest beauty and wellness guidance, curated picks, and clear reasons why they're worth considering.
                            </p>
                            
                            <a 
                                href="http://eepurl.com/jpTPXQ"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#ab3d22] hover:bg-[#8a2f1a] text-white font-semibold rounded-lg transition-colors"
                            >
                                Subscribe
                                <ChevronRight className="h-4 w-4" />
                            </a>
                            
                            <p className="text-gray-500 text-sm mt-4">
                                No noise. Just clarity.
                            </p>
                        </div>

                        {/* RIGHT HALF: Links */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 text-sm">
                            {/* Explore */}
                            <div>
                                <div className="font-semibold text-white mb-4">Explore</div>
                                <ul className="space-y-2.5 text-gray-400">
                                    {categories && categories.length > 0 ? (
                                        categories.slice(0, 6).map(category => (
                                            <li key={category._id}>
                                                <Link to={`/category/${category.slug}`} className="hover:text-white transition-colors">
                                                    {category.name}
                                                </Link>
                                            </li>
                                        ))
                                    ) : (
                                        <>
                                            <li><Link to="/category/skincare" className="hover:text-white transition-colors">Skincare</Link></li>
                                            <li><Link to="/category/hair" className="hover:text-white transition-colors">Hair</Link></li>
                                            <li><Link to="/category/makeup" className="hover:text-white transition-colors">Makeup</Link></li>
                                            <li><Link to="/category/body" className="hover:text-white transition-colors">Body</Link></li>
                                            <li><Link to="/category/wellness" className="hover:text-white transition-colors">Wellness</Link></li>
                                            <li><Link to="/category/health" className="hover:text-white transition-colors">Health</Link></li>
                                        </>
                                    )}
                                    <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                                </ul>
                            </div>

                            {/* Company */}
                            <div>
                                <div className="font-semibold text-white mb-4">Company</div>
                                <ul className="space-y-2.5 text-gray-400">
                                    <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                                    <li><Link to="/editorial-standards" className="hover:text-white transition-colors">Editorial Standards</Link></li>
                                    <li><Link to="/affiliate-disclosure" className="hover:text-white transition-colors">Affiliate Disclosure</Link></li>
                                    <li><Link to="/advertise" className="hover:text-white transition-colors">Advertise</Link></li>
                                    <li><Link to="/terms" className="hover:text-white transition-colors">Terms</Link></li>
                                    <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link></li>
                                </ul>
                            </div>

                            {/* Connect */}
                            <div>
                                <div className="font-semibold text-white mb-4">Connect</div>
                                <ul className="space-y-2.5 text-gray-400">
                                    <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    {/* Bottom row */}
                    <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
                        <p>&copy; 2025 Radiance. All rights reserved.</p>
                    </div>
                </Container>
            </footer>
        </div>
    );
};

export default HomePage;
